import React, { useState } from 'react';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { auth, LoginRequest, AuthResponse } from '../api/authApi';
import { GoogleLoginButton } from './GoogleLoginButton';
import { AccountLinkingModal } from './AccountLinkingModal';
import { Loader } from '@mantine/core';
import './Auth.css';
import LogoSvg from '../assets/logo.svg';

interface LoginFormProps {
  onLogin: (user: AuthResponse['user'], token: string) => void;
  onSwitchToRegister: () => void;
  onSwitchToPasswordReset: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onLogin, onSwitchToRegister, onSwitchToPasswordReset }) => {
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Google login and account linking state
  const [showLinkingModal, setShowLinkingModal] = useState(false);
  const [linkingData, setLinkingData] = useState<{
    userId: number;
    googleToken: string;
    userEmail?: string;
  } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const data = await auth.login(formData);
      
      // Store token and user data as per server specification
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('currentUser', JSON.stringify(data.user)); // Keep for compatibility
      
      // Call parent callback
      onLogin(data.user, data.token);
    } catch (error: unknown) {
      console.error('Login error:', error);
      setError(error instanceof Error ? error.message : 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = (user: any, token: string) => {
    onLogin(user, token);
  };

  const handleGoogleError = (error: string) => {
    setError(error);
  };

  const handleAccountLinkingRequired = (userId: number, googleToken: string) => {
    setLinkingData({ userId, googleToken });
    setShowLinkingModal(true);
  };

  const handleLinkingSuccess = (user: any, token: string) => {
    setShowLinkingModal(false);
    setLinkingData(null);
    onLogin(user, token);
  };

  const handleLinkingClose = () => {
    setShowLinkingModal(false);
    setLinkingData(null);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: '30px', marginBottom: '12px' }}>
            <img src={LogoSvg} alt="SymphonyTodo Logo" style={{ height: 32, width: 32, display: 'block' }} />
          </div>
          <h1>Hi there! :)</h1>
          <p>Sign in to your SymphonyTodo account</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-wrapper">
              <Mail size={18} className="input-icon" />
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
                autoComplete="email"
                autoCorrect="off"
                spellCheck="false"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <Lock size={18} className="input-icon" />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                autoCorrect="off"
                spellCheck="false"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="auth-submit-btn"
            disabled={isLoading || !formData.email || !formData.password}
          >
            {isLoading ? <Loader type="bars" size="sm" color="white" /> : 'Sign In'}
          </button>
          
          <div className="auth-divider" style={{ margin: '24px 0', textAlign: 'center', position: 'relative' }}>
            <span style={{ 
              backgroundColor: '#fff', 
              padding: '0 16px', 
              color: '#6b7280', 
              fontSize: '14px',
              position: 'relative',
              zIndex: 1
            }}>
              or
            </span>
            <div style={{ 
              position: 'absolute', 
              top: '50%', 
              left: 0, 
              right: 0, 
              height: '1px', 
              backgroundColor: '#e5e7eb',
              zIndex: 0
            }} />
          </div>

          <GoogleLoginButton
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            onAccountLinkingRequired={handleAccountLinkingRequired}
            disabled={isLoading}
            text="Continue with Google"
          />
          
          <div className="forgot-password-link">
            <button 
              type="button" 
              className="auth-link"
              onClick={onSwitchToPasswordReset}
            >
              Forgot your password?
            </button>
          </div>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <button
              type="button"
              className="auth-link"
              onClick={onSwitchToRegister}
            >
              Sign up
            </button>
          </p>
        </div>
      </div>
      
      {/* Account Linking Modal */}
      {showLinkingModal && linkingData && (
        <AccountLinkingModal
          isOpen={showLinkingModal}
          onClose={handleLinkingClose}
          onSuccess={handleLinkingSuccess}
          userId={linkingData.userId}
          googleToken={linkingData.googleToken}
          userEmail={linkingData.userEmail}
        />
      )}
    </div>
  );
};
